import { jsx } from "panel";
import last from "lodash/last";
import sum from "lodash/sum";
import { SegmentationLimitInfoEl, SegmentationLimitLearnMoreUrl } from "common/charts/misc/segmentation-limit-info";
import { ButtonEl } from "common/components/button";
import { ButtonTheme } from "common/components/button/constants";
import { CheckboxEl } from "common/components/checkbox";
import {
  CHECKBOX_CELL_PADDING_LEFT,
  CssClass,
  DEFAULT_TOOLTIP_MAX_WIDTH,
  FOOTER_ROW_HEIGHT,
  GROUP_HEADER_MIN_WIDTH,
  MIN_DATA_COLUMNS_VIEWPORT_WIDTH,
  PLACEHOLDER_GRID_AREA_OFFSET,
  STICKY_COLUMNS_BORDER_WIDTH
} from "common/components/data-table/constants";
import { SelectTheme } from "common/components/select/constants";
import { SelectEl } from "common/components/select";
import { SvgIconEl } from "common/components/svg-icon";
import { ExperimentalTooltipEl, TooltipEl } from "common/components/tooltip";
import { Placement } from "common/components/tooltip/types";
import { SortBy, SortOrder } from "common/report/util/sorting/types";
import { isURL } from "common/util/url";
import { renderSegmentLabel } from "common/templates/mixins";
import { InputEl } from "common/components/input";
import { InputSize, InputType } from "common/components/input/types";
import { BannerEl } from "common/components/banner";
import { BannerSize, BannerTheme, BannerType } from "common/components/banner/types";
import { HORIZONTAL_CONTENT_PADDING_STYLE } from "common/widgets/sidenav-layout/content-frame";
import { FilterBarEl } from "common/components/filter-bar";
import { getTooltipFromLabel } from "common/report/util/properties";
import { commaizeNumber, formatPercent } from "common/util";
import { SIZE, TextHighlightEl, THEME } from "common/components/text-highlight";
import { LIFT_TOOLTIP_COMPARE, LiftType } from "common/report/util/lift/types";
import { formatDateRanges } from "common/report/insights/util";
import {
  CommonDataTableFields,
  isTimeComparisonColumnType,
  RowType,
  TimeComparisonColumnType,
  TruncatableTextType
} from "./types";
import {
  areIntersectingRanges,
  getSummedColumnWidth,
  getSummedRowHeight,
  hasSegmentMetricMetadata,
  isNumberStyleColumn,
  isSameCellByContext,
  isSameRowByContext,
  isSegmentColumn,
  isTextStyleColumn
} from "./utils";
import segmentMetricOverflowTooltipStyle from "./segment-metric-overflow-tooltip.inline.styl";
import viewNMenuStyle from "./view-n-menu.inline.styl";
function renderViewNMenu({ tableEl, segmentIdx }) {
  const isColumnSort = tableEl.state.sorting.sortBy === SortBy.Column;
  const hasViewNLimit = tableEl.hasViewNLimit({ segmentIdx });
  return /* @__PURE__ */ jsx("div", {
    sel: ".view-n-menu-content"
  }, /* @__PURE__ */ jsx("style", null, viewNMenuStyle), /* @__PURE__ */ jsx("div", {
    sel: ".view-n-menu-limit-section"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".view-n-menu-title"
  }, `${tableEl.getViewNSortOrderLabel({ segmentIdx })} ${isColumnSort ? `Segments` : `Values`}`), /* @__PURE__ */ jsx("div", {
    sel: ".view-n-menu-description"
  }, `Choose the number of ${isColumnSort ? `segments` : `rows`} you want to see in the chart`), /* @__PURE__ */ jsx("div", {
    sel: ".view-n-menu-input-section",
    class: { "show-all-mode": !hasViewNLimit }
  }, /* @__PURE__ */ jsx(InputEl, {
    sel: ".view-n-menu-input",
    attrs: {
      "auto-focus": hasViewNLimit,
      "not-focusable": !hasViewNLimit,
      size: InputSize.Medium,
      type: InputType.Number,
      min: `1`,
      maxlength: 5
    },
    on: {
      focus: () => tableEl.handleViewNLimitInputFocus({ segmentIdx }),
      blur: () => tableEl.handleViewNLimitInputBlur(),
      change: (ev) => tableEl.handleViewNLimitInputChange({ segmentIdx, value: ev.target.value })
    },
    props: {
      value: tableEl.getViewNLimitInputValue({ segmentIdx }).toString()
    }
  }), /* @__PURE__ */ jsx("div", {
    sel: ".view-n-menu-input-label"
  }, isColumnSort ? `segments` : `rows`))), /* @__PURE__ */ jsx("div", {
    sel: ".view-n-show-all-section"
  }, /* @__PURE__ */ jsx(CheckboxEl, {
    attrs: {
      checked: !hasViewNLimit
    },
    on: {
      click: () => tableEl.handleViewNShowAllClick({ segmentIdx })
    }
  }, "Show all ", isColumnSort ? `segments` : `rows`)));
}
function renderViewNControl({ tableEl, segmentIdx }) {
  const { sortColumn, sortOrder } = tableEl.getViewNSortInfo({ segmentIdx });
  const sortFieldStr = isSegmentColumn(sortColumn) ? sortColumn.title : sortColumn.sortSelectTitle;
  const sortOrderStr = sortOrder === SortOrder.Ascending ? `ascending` : `descending`;
  return /* @__PURE__ */ jsx(SelectEl, {
    sel: ".view-n-select",
    attrs: {
      "experimental-positioning": true,
      theme: SelectTheme.Tertiary,
      "always-show-caret": true,
      disabled: tableEl.isBoardMode(),
      label: tableEl.getViewNLabel({ segmentIdx }),
      ...tableEl.hasViewNLimit({ segmentIdx }) && {
        tooltip: {
          placement: Placement.RightCenter,
          "max-width": `${DEFAULT_TOOLTIP_MAX_WIDTH}px`,
          text: `Sorted by ${sortFieldStr} ${sortOrderStr}`
        }
      }
    },
    props: {
      dropdownBodyRenderer: () => renderViewNMenu({ tableEl, segmentIdx })
    },
    on: {
      menuChange: (ev) => tableEl.handleViewNMenuChange({ ev, segmentIdx })
    }
  });
}
function TitleCell({ tableEl, column }) {
  if (isTimeComparisonColumnType(column) && column.timeComparisonMetadata.timeComparisonColumnType === TimeComparisonColumnType.Past) {
    return null;
  }
  const context = { rowType: RowType.Header, columnIdx: column.columnIdx };
  const isHoveredCell = isSameCellByContext(context, tableEl.state.hoveredCellContext);
  const { sorting } = tableEl.state;
  const isCurrSortField = sorting.sortBy === SortBy.Value && sorting.valueField === column.field;
  let headerTooltipEl = null;
  if (isHoveredCell) {
    let tooltipContent = null;
    if (column.tooltip) {
      tooltipContent = column.tooltip;
    } else if (tableEl.state.hoveredTruncatedTextType === TruncatableTextType.HeaderTitle) {
      tooltipContent = column.title;
    } else if (tableEl.state.hoveredTruncatedTextType === TruncatableTextType.HeaderSubtitle) {
      tooltipContent = column.subtitle;
    }
    headerTooltipEl = tooltipContent ? /* @__PURE__ */ jsx(TooltipEl, {
      attrs: {
        "max-width": !column.sticky && column.subsetColumnIdx === 0 ? `${column.computedWidth}px` : `${DEFAULT_TOOLTIP_MAX_WIDTH}px`,
        placement: Placement.Top,
        "always-show": true,
        "allowed-flip-directions": []
      }
    }, tooltipContent) : null;
  }
  const titleEl = /* @__PURE__ */ jsx("div", {
    class: { [CssClass.HeaderTitleText]: true, "cell-text": true },
    on: {
      pointerenter: (ev) => tableEl.helpers.handleTruncatableTextPointerEnter(ev, TruncatableTextType.HeaderTitle),
      pointerleave: () => tableEl.helpers.handleTruncatableTextPointerLeave()
    }
  }, column.titlePrefix ? /* @__PURE__ */ jsx("span", {
    sel: ".header-title-prefix"
  }, column.titlePrefix) : null, /* @__PURE__ */ jsx("span", {
    attrs: { elref: `headerCellTitle` }
  }, column.title));
  let subtitleEl = null;
  if (column.subtitle) {
    subtitleEl = /* @__PURE__ */ jsx("div", {
      class: { [CssClass.HeaderSubtitleText]: true, "cell-text": true },
      on: {
        pointerenter: (ev) => tableEl.helpers.handleTruncatableTextPointerEnter(ev, TruncatableTextType.HeaderSubtitle),
        pointerleave: () => tableEl.helpers.handleTruncatableTextPointerLeave()
      }
    }, column.subtitle);
  } else if (isSegmentColumn(column) && tableEl.canRenderViewNControl({ segmentColumn: column })) {
    subtitleEl = renderViewNControl({ tableEl, segmentIdx: column.segmentIndex });
  } else if (tableEl.hasColumnWithSubtitle()) {
    subtitleEl = /* @__PURE__ */ jsx("div", {
      sel: ".header-subtitle-placeholder"
    });
  }
  let titleLabelContents = null;
  if (column.title) {
    if (!column.isSortable) {
      titleLabelContents = titleEl;
    } else if (isSegmentColumn(column)) {
      titleLabelContents = /* @__PURE__ */ jsx(SelectEl, {
        attrs: {
          "menu-width": 190,
          "experimental-positioning": true
        },
        on: {
          change: tableEl.helpers.handleSortSelect
        },
        props: {
          sections: tableEl.getSortSelectSections(column)
        }
      }, /* @__PURE__ */ jsx("div", {
        attrs: { slot: `trigger` },
        sel: ".sort-label.within-select-el"
      }, titleEl, isSegmentColumn(column) ? /* @__PURE__ */ jsx("span", {
        sel: ".segment-cardinality"
      }, column.segmentCardinality) : null, /* @__PURE__ */ jsx("div", {
        sel: ".sort-icon"
      }, /* @__PURE__ */ jsx(SvgIconEl, {
        attrs: { icon: `caret-down` }
      }))));
    } else {
      titleLabelContents = /* @__PURE__ */ jsx("div", {
        sel: ".sort-label.value-sort"
      }, titleEl, isCurrSortField ? sorting.sortOrder === SortOrder.Ascending ? /* @__PURE__ */ jsx("div", {
        sel: ".sort-icon"
      }, /* @__PURE__ */ jsx(SvgIconEl, {
        attrs: { icon: `sort-value-asc` }
      })) : /* @__PURE__ */ jsx("div", {
        sel: ".sort-icon"
      }, /* @__PURE__ */ jsx(SvgIconEl, {
        attrs: { icon: `sort-value-desc` }
      })) : null);
    }
  }
  const hasSelectAllCheckbox = column.hasCheckbox;
  const titleContents = /* @__PURE__ */ jsx("div", {
    sel: ".mp-table-title-wrapper",
    on: {
      ...isSegmentColumn(column) ? {} : { click: () => tableEl.helpers.handleHeaderTitleClick(column) }
    }
  }, hasSelectAllCheckbox ? (() => {
    const { numSelected, atSelectionLimit } = tableEl.helpers.getGlobalSelectionStats();
    return /* @__PURE__ */ jsx("div", {
      sel: ".checkbox-wrapper"
    }, /* @__PURE__ */ jsx(CheckboxEl, {
      sel: ".select-segment-checkbox",
      attrs: {
        checked: atSelectionLimit,
        "unchecked-icon": numSelected > 0 ? `dash` : ``
      },
      style: { width: `auto` },
      on: {
        click: tableEl.helpers.toggleAllSelected,
        pointerenter: tableEl.helpers.handleSelectAllPointerEnter,
        pointerleave: tableEl.helpers.handleSelectAllPointerLeave
      }
    }), tableEl.state.isSelectAllHovered ? /* @__PURE__ */ jsx(TooltipEl, {
      attrs: { placement: Placement.RightCenter, "always-show": true }
    }, atSelectionLimit ? `Deselect All` : `Select Top Segments`) : null);
  })() : null, titleLabelContents, column.icon ? /* @__PURE__ */ jsx("div", {
    sel: ".mp-table-title-cell-icon"
  }, /* @__PURE__ */ jsx(SvgIconEl, {
    attrs: { icon: column.icon }
  })) : null);
  const gridCellWidth = isTimeComparisonColumnType(column) && column.timeComparisonMetadata.timeComparisonColumnType === TimeComparisonColumnType.Current ? 2 : 1;
  const gridRowStart = (Number.isFinite(tableEl.getPreTitleRowHeight()) ? 1 : 0) + 1;
  const gridColStart = column.subsetColumnIdx + 1;
  const gridAreaStyle = `${gridRowStart} / ${gridColStart} / ${gridRowStart + 1} / ${gridColStart + gridCellWidth}`;
  return /* @__PURE__ */ jsx("div", {
    sel: ".mp-table-cell.title-cell",
    class: {
      "mp-table-sortable-column": column.isSortable,
      "mp-text-cell": isTextStyleColumn(column),
      "mp-number-cell": isNumberStyleColumn(column),
      "segment-col": isSegmentColumn(column),
      "first-segment-col": column.field === `${CommonDataTableFields.SegmentPrefix}0`,
      "first-in-group": column.first,
      "last-in-group": column.last,
      "overall-conversion-header": column.field === `overall`,
      "first-column": column.leftmost,
      "last-column": column.rightmost,
      "last-sticky": column.lastSticky,
      "last-segment-col": isSegmentColumn(column) && column.lastSegment,
      "sticky-column": column.sticky,
      "has-tooltip": Boolean(headerTooltipEl || tableEl.state.isSelectAllHovered),
      "curr-sort-field": isCurrSortField,
      "has-subtitle": Boolean(subtitleEl)
    },
    style: {
      "grid-area": gridAreaStyle
    },
    on: {
      pointerenter: () => tableEl.helpers.handleCellPointerEnter(context),
      pointerleave: tableEl.helpers.handleCellPointerLeave
    },
    attrs: {
      title: column.label || ``
    }
  }, titleContents, subtitleEl, headerTooltipEl);
}
function CellTypeCheckbox({
  row,
  tableEl,
  atSelectionLimit
}) {
  const options = tableEl.helpers.getCheckboxState({ row, atSelectionLimit });
  return !row.isComparisonRow && !Number.isFinite(row.groupedRowIdx) ? /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx(CheckboxEl, {
    sel: ".select-segment-checkbox",
    attrs: options.attrs,
    on: { click: (ev) => !options.attrs.disabled && tableEl.helpers.toggleSelected(ev, row) }
  }), options.selectDisabledReason && options.attrs.disabled ? /* @__PURE__ */ jsx(TooltipEl, {
    attrs: { placement: Placement.RightCenter }
  }, options.selectDisabledReason) : null) : /* @__PURE__ */ jsx("div", {
    sel: ".checkbox-placeholder"
  });
}
function CellTypeCollapsible({ row, tableEl }) {
  return /* @__PURE__ */ jsx("div", {
    sel: ".mp-table-collapsible-wrapper"
  }, !Number.isFinite(row.groupedRowIdx) ? /* @__PURE__ */ jsx(ButtonEl, {
    attrs: {
      icon: row.isCollapsed ? `caret-right` : `caret-down`,
      theme: ButtonTheme.Tertiary
    },
    on: {
      click: (ev) => tableEl.helpers.collapsibleClicked(ev, row)
    }
  }) : null);
}
function commaizeAndFormatAsDecimal(number, formatAsDecimal) {
  return formatAsDecimal ? commaizeNumber(number, { maxPrecision: 2, exactPrecision: true }) : commaizeNumber(number);
}
function formatPercentForDataTable(percent) {
  return formatPercent(percent, 2, true);
}
function renderSegmentMetricMetadataRow({
  metadata,
  formatAsDecimal,
  liftType,
  metricTitle,
  prefix,
  inlineMetric = false
}) {
  const { comparisonOverall, liftOverall, metricOverall } = metadata;
  const formattedMetricOverall = commaizeAndFormatAsDecimal(metricOverall, formatAsDecimal);
  const formattedComparisonOverall = commaizeAndFormatAsDecimal(comparisonOverall, formatAsDecimal);
  const additionalSummaryText = Number.isFinite(liftOverall) ? ` (${LIFT_TOOLTIP_COMPARE} ${formattedComparisonOverall})` : ``;
  return /* @__PURE__ */ jsx("div", {
    sel: ".segment-metric-total-row"
  }, /* @__PURE__ */ jsx("span", {
    sel: ".segment-metric-total-label"
  }, prefix, inlineMetric ? ` ${metricTitle}` : ``), /* @__PURE__ */ jsx("span", {
    sel: ".segment-metric-total-value"
  }, formattedMetricOverall), Number.isFinite(liftOverall) && /* @__PURE__ */ jsx(TextHighlightEl, {
    sel: ".segment-metric-lift-value",
    attrs: {
      text: `${liftOverall > 0 && liftType !== LiftType.Overall ? `+` : ``}${formatPercentForDataTable(
        liftOverall
      )}`,
      theme: liftOverall === 0 || liftType === LiftType.Overall ? THEME.NEUTRAL : liftOverall > 0 ? THEME.POSITIVE : THEME.NEGATIVE,
      size: SIZE.SMALL
    }
  }), inlineMetric ? additionalSummaryText : /* @__PURE__ */ jsx(ExperimentalTooltipEl, {
    attrs: {
      placement: Placement.RightCenter,
      text: `_${prefix} ${metricTitle}_ ${formattedMetricOverall}${additionalSummaryText}`
    }
  }));
}
function renderSegmentMetricMetadatas({
  dataColumns,
  liftType,
  metricMetadatas,
  numAvailableRows
}) {
  const metadataEntries = metricMetadatas.map((metadata, index) => {
    return {
      formatAsDecimal: dataColumns[index].hasDecimal,
      liftType,
      metadata,
      metricTitle: dataColumns[index].title,
      prefix: dataColumns[index].titlePrefix
    };
  }).filter((entry) => hasSegmentMetricMetadata(entry.metadata));
  const hasHiddenRows = metadataEntries.length > numAvailableRows;
  const numMetricRowsToRender = hasHiddenRows ? numAvailableRows - 1 : metricMetadatas.length;
  const visibleMetadatas = metadataEntries.slice(0, numMetricRowsToRender);
  const hiddenMetadatas = metadataEntries.slice(numMetricRowsToRender);
  return /* @__PURE__ */ jsx("div", {
    sel: ".segment-metric-totals-section"
  }, visibleMetadatas.map(renderSegmentMetricMetadataRow), (hiddenMetadatas == null ? void 0 : hiddenMetadatas.length) > 0 ? /* @__PURE__ */ jsx("div", {
    sel: ".segment-metric-total-more-section"
  }, /* @__PURE__ */ jsx("span", {
    sel: ".segment-metric-total-more"
  }, hiddenMetadatas.length, " more"), /* @__PURE__ */ jsx(ExperimentalTooltipEl, {
    attrs: {
      placement: Placement.RightCenter
    }
  }, /* @__PURE__ */ jsx("div", {
    sel: ".segment-metric-overflow-content"
  }, /* @__PURE__ */ jsx("style", null, segmentMetricOverflowTooltipStyle), hiddenMetadatas.map((entry) => renderSegmentMetricMetadataRow({ ...entry, inlineMetric: true }))))) : null);
}
function renderDataCellContent({
  liftType,
  value,
  liftValue,
  comparisonValue,
  formatAsDecimal
}) {
  if (value === void 0) {
    return `-`;
  }
  const formattedValue = commaizeAndFormatAsDecimal(value, formatAsDecimal);
  if (Number.isFinite(liftValue)) {
    const formattedLiftValue = formatPercentForDataTable(liftValue);
    const formattedComparisonValue = commaizeAndFormatAsDecimal(comparisonValue, formatAsDecimal);
    const comparisonTooltip = /* @__PURE__ */ jsx(ExperimentalTooltipEl, {
      attrs: {
        placement: Placement.Top,
        text: `${formattedValue} ${LIFT_TOOLTIP_COMPARE} ${formattedComparisonValue}`
      }
    });
    if (liftType === LiftType.Overall) {
      return /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
        sel: ".cell-text"
      }, /* @__PURE__ */ jsx("span", {
        sel: ".cell-text.is-secondary"
      }, `${formattedValue} \xB7 `), comparisonTooltip, `${formattedLiftValue}`));
    }
    const isPositive = liftValue > 0;
    return /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
      sel: ".cell-text"
    }, comparisonTooltip, formattedValue, /* @__PURE__ */ jsx(TextHighlightEl, {
      sel: ".mp-lift-pill",
      attrs: {
        text: `${isPositive ? `+` : ``}${formattedLiftValue}`,
        theme: liftValue === 0 ? THEME.NEUTRAL : isPositive ? THEME.POSITIVE : THEME.NEGATIVE,
        size: SIZE.SMALL
      }
    })));
  }
  return formattedValue;
}
function TimeComparisonHeaderCell({ column, tableEl }) {
  const gridCellWidth = 1;
  const gridRowStart = tableEl.getNumHeaderRows();
  const gridColStart = column.subsetColumnIdx + 1;
  const gridAreaStyle = `${gridRowStart} / ${gridColStart} / ${gridRowStart + 1} / ${gridColStart + gridCellWidth}`;
  let formattedDateRange = ``;
  let prefix = ``;
  let titleAttributeText = ``;
  if (column.timeComparisonMetadata) {
    [formattedDateRange] = formatDateRanges([column.timeComparisonMetadata.dateRange]);
    prefix = column.timeComparisonMetadata.timeComparisonColumnType === TimeComparisonColumnType.Current ? `Current` : `Past`;
    titleAttributeText = `${prefix} ${formattedDateRange}`;
  }
  const context = {
    columnIdx: column.columnIdx,
    rowType: RowType.TimeComparisonHeader
  };
  return /* @__PURE__ */ jsx("div", {
    attrs: { title: titleAttributeText },
    class: {
      "first-in-group": column.first,
      "last-in-group": column.last,
      "first-column": column.leftmost,
      "last-column": column.rightmost,
      "last-sticky": column.lastSticky
    },
    on: {
      pointerenter: () => tableEl.helpers.handleCellPointerEnter(context),
      pointerleave: tableEl.helpers.handleCellPointerLeave
    },
    sel: ".mp-table-cell.mp-time-comparison-cell",
    style: { "grid-area": gridAreaStyle }
  }, column.timeComparisonMetadata ? /* @__PURE__ */ jsx("div", {
    sel: ".time-comparison-cell-wrapper"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".time-comparison-cell-content"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".overflow-wrapper"
  }, /* @__PURE__ */ jsx("span", {
    sel: ".mp-time-comparison-prefix"
  }, prefix), formattedDateRange))) : null);
}
function renderBodyRows({
  tableEl,
  rows,
  columns,
  atSelectionLimit
}) {
  return rows.map((row) => {
    return columns.map((column) => {
      return /* @__PURE__ */ jsx(BodyCell, {
        tableEl,
        row,
        column,
        atSelectionLimit
      });
    });
  });
}
function BodyCell({
  tableEl,
  row,
  column,
  atSelectionLimit,
  segmentCellBlock
}) {
  var _a;
  const context = tableEl.helpers.getBodyCellRenderContext(row, column);
  const cellItem = tableEl.helpers.getCellItem(row, column.columnIdx);
  const empty = cellItem === null;
  const bgColor = cellItem && cellItem.backgroundColor || column.backgroundColor || null;
  const textColor = cellItem && cellItem.textColor || column.textColor || null;
  const tableTheme = tableEl.getTheme();
  const focusable = tableTheme.cellFocusable && !empty;
  let cellContent;
  let cellTooltipEl = null;
  let cellCheckboxEl = null;
  let cellCollapseEl = null;
  let isUrlSegmentCell = false;
  if (column.cellRenderer) {
    cellContent = column.cellRenderer({ item: row, context });
  } else if (isSegmentColumn(column)) {
    if (isURL(context.label)) {
      cellContent = renderSegmentLabel(context.label);
      isUrlSegmentCell = true;
    } else {
      cellContent = context.label;
    }
  } else {
    if (tableEl.isMultimetricMode()) {
      cellContent = renderDataCellContent({
        liftType: tableEl.attr(`lift-comparison-type`),
        value: row[context.field],
        liftValue: (_a = row[context.liftField]) != null ? _a : null,
        comparisonValue: row[context.liftExtra],
        formatAsDecimal: column.hasDecimal
      });
    } else {
      cellContent = context.label;
    }
  }
  if (isSameCellByContext(context, tableEl.state.hoveredCellContext)) {
    let tooltipContent = null;
    const defaultTooltip = getTooltipFromLabel(context.label);
    if (cellItem == null ? void 0 : cellItem.tooltip) {
      tooltipContent = cellItem.tooltip;
    } else if (isSegmentColumn(column)) {
      const isUnderSegmentCellBlock = tableEl.showSegmentCellBlocks() && !segmentCellBlock && !column.lastSegment;
      if (isUnderSegmentCellBlock) {
        tooltipContent = null;
      } else if (tableEl.state.hoveredTruncatedTextType) {
        tooltipContent = isUrlSegmentCell ? context.label : defaultTooltip || cellContent;
      } else {
        tooltipContent = defaultTooltip;
      }
    }
    const stringTooltip = typeof tooltipContent === `string`;
    cellTooltipEl = Boolean(tooltipContent) && /* @__PURE__ */ jsx(ExperimentalTooltipEl, {
      attrs: {
        "always-show": true,
        "max-width": `${DEFAULT_TOOLTIP_MAX_WIDTH}px`,
        placement: Placement.RightCenter,
        "break-word": true,
        text: stringTooltip ? tooltipContent : ``
      }
    });
  }
  if (column.field !== `statSig`) {
    cellContent = /* @__PURE__ */ jsx("div", {
      class: { [CssClass.OverflowWrapper]: true },
      on: {
        pointerenter: (ev) => tableEl.helpers.handleTruncatableTextPointerEnter(ev),
        pointerleave: () => tableEl.helpers.handleTruncatableTextPointerLeave()
      }
    }, cellContent);
  }
  if (column.hasCheckbox) {
    cellCheckboxEl = /* @__PURE__ */ jsx(CellTypeCheckbox, {
      row,
      tableEl,
      atSelectionLimit
    });
  }
  if (column.hasCollapseButton) {
    cellCollapseEl = /* @__PURE__ */ jsx(CellTypeCollapsible, {
      row,
      tableEl
    });
  }
  let gridAreaStyle = null;
  if (!segmentCellBlock) {
    const gridRowStart = tableEl.getNumHeaderRows() + PLACEHOLDER_GRID_AREA_OFFSET + row.renderRowIdx + 1;
    const gridColStart = column.subsetColumnIdx + 1;
    gridAreaStyle = `${gridRowStart} / ${gridColStart} / ${gridRowStart + 1} / ${gridColStart + 1}`;
  }
  const isSelectedCell = focusable && tableEl.isSelectedCell(context);
  const isSelectable = column.isSelectable && !tableEl.isBoardMode();
  return /* @__PURE__ */ jsx("div", {
    sel: ".mp-table-cell.body-cell",
    key: `${row.rowIdx}:${row.groupedRowIdx}:${column.columnIdx}:${context.label}`,
    class: {
      "first-in-group": column.first,
      "last-in-group": column.last,
      "first-column": column.leftmost,
      "last-column": column.rightmost,
      "last-sticky": column.lastSticky,
      "sticky-column": column.sticky,
      "last-segment-col": isSegmentColumn(column) && column.lastSegment,
      "segment-col": isSegmentColumn(column),
      "segment-start-cell": context.segmentStartCell,
      "segment-end-cell": context.segmentEndCell,
      "segment-start-row": row.segmentStartRow,
      "segment-end-row": row.segmentEndRow,
      "first-segment-col": column.field === `${CommonDataTableFields.SegmentPrefix}0`,
      "mp-text-cell": isTextStyleColumn(column),
      "mp-number-cell": isNumberStyleColumn(column),
      "has-multi-select": tableEl.attr(`multi-select`),
      "first-in-pair": tableEl.attr(`has-comparison`) && !row.isComparisonRow,
      "hovered-row": tableEl.canHoverRows() && isSameRowByContext(context, tableEl.state.hoveredCellContext),
      "selected-cell": isSelectedCell,
      "is-empty": empty,
      "segment-block-label": Boolean(segmentCellBlock),
      selectable: isSelectable
    },
    attrs: {
      elref: `bodyCell`,
      ...focusable ? { tabindex: 0 } : {}
    },
    style: {
      "--bg-color": bgColor,
      color: textColor,
      cursor: cellItem && cellItem.clickable || isSelectable ? `pointer` : `auto`,
      ...gridAreaStyle ? { "grid-area": gridAreaStyle } : {}
    },
    on: {
      click: (ev) => tableEl.helpers.handleCellClick(ev, row, column.field, context, {
        textColor,
        bgColor
      }),
      pointerenter: () => tableEl.helpers.handleCellPointerEnter(context),
      pointerleave: tableEl.helpers.handleCellPointerLeave
    }
  }, /* @__PURE__ */ jsx("div", {
    sel: ".body-cell-wrapper"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".body-cell-content"
  }, cellCheckboxEl, cellCollapseEl, cellContent, isSelectedCell === false && cellTooltipEl), (() => {
    if (!segmentCellBlock) {
      return null;
    }
    if (!segmentCellBlock.metricMetadatas) {
      return null;
    }
    const numDescendantRows = segmentCellBlock.flatRowEndIdx - segmentCellBlock.flatRowStartIdx;
    if (numDescendantRows <= 1) {
      return null;
    }
    return renderSegmentMetricMetadatas({
      dataColumns: tableEl.getCurrentDataColumns(),
      liftType: tableEl.attr(`lift-comparison-type`),
      metricMetadatas: segmentCellBlock.metricMetadatas,
      numAvailableRows: numDescendantRows
    });
  })()));
}
function renderResizeHandles({
  tableEl,
  columns,
  gap,
  idxOffset,
  resizingColumnIdx,
  resizeHandleTopOffset
}) {
  return columns.reduce((handles, column, idx) => {
    if (!column.resizable) {
      return handles;
    }
    const handleWidth = 8;
    const globalColIdx = idx + idxOffset;
    const isResizing = resizingColumnIdx === globalColIdx;
    let handleLeft = 0;
    if (gap) {
      const gapBefore = !column.lastSticky && !column.rightmost ? gap : 0;
      handleLeft = -gapBefore - handleWidth / 2;
    } else {
      const hasStickyColumnsBorder = column.lastSticky;
      handleLeft = -(handleWidth / 2) - (hasStickyColumnsBorder ? STICKY_COLUMNS_BORDER_WIDTH / 2 : 0);
    }
    return handles.concat(
      /* @__PURE__ */ jsx("div", {
        sel: `.${CssClass.ColumnResizeHandle}`,
        class: { "is-resizing": isResizing, "other-resizing": Number.isFinite(resizingColumnIdx) && !isResizing },
        style: {
          "grid-column-start": `${idx + 2}`,
          left: `${handleLeft}px`,
          width: `${handleWidth}px`,
          top: `${resizeHandleTopOffset}px`,
          height: `calc(100% - ${resizeHandleTopOffset}px)`
        },
        on: {
          mousedown: (ev) => tableEl.onResizeMouseDown({ ev, resizeColumnIndex: globalColIdx })
        }
      })
    );
  }, []);
}
function renderSegmentCellBlocks({ tableEl }) {
  if (!tableEl.showSegmentCellBlocks()) {
    return [];
  }
  const { atSelectionLimit } = tableEl.helpers.getGlobalSelectionStats();
  const numHeaderRows = tableEl.getNumHeaderRows();
  const { renderRowStartIdx, numRowsToRender } = tableEl.state;
  const renderRowEndIdx = renderRowStartIdx + numRowsToRender;
  return tableEl.state.segmentCellBlocks.filter((cellBlock) => {
    return areIntersectingRanges(
      [cellBlock.flatRowStartIdx, cellBlock.flatRowEndIdx],
      [renderRowStartIdx, renderRowEndIdx]
    );
  }).map((cellBlock) => {
    var _a;
    const gridRowOffset = numHeaderRows + PLACEHOLDER_GRID_AREA_OFFSET;
    const gridRowStartIdx = gridRowOffset + Math.max(cellBlock.flatRowStartIdx - renderRowStartIdx, 0);
    const gridRowEndIdx = gridRowOffset + Math.min(cellBlock.flatRowEndIdx - renderRowStartIdx, numRowsToRender);
    const column = tableEl.state.columns[cellBlock.columnIdx];
    const row = tableEl.state.rows[cellBlock.rowStartIdx];
    const hoveredRowIndex = (_a = tableEl.state.hoveredCellContext) == null ? void 0 : _a.rowIdx;
    const isHovered = hoveredRowIndex !== void 0 && hoveredRowIndex >= cellBlock.rowStartIdx && hoveredRowIndex < cellBlock.rowEndIdx;
    return /* @__PURE__ */ jsx("div", {
      sel: `.segment-cell-block`,
      style: {
        "grid-row": `${gridRowStartIdx + 1} / ${gridRowEndIdx + 1}`,
        "grid-column": `${cellBlock.columnIdx + 1} / ${cellBlock.columnIdx + 2}`
      },
      class: {
        "first-col": cellBlock.columnIdx === 0,
        "hovered-segment-block": isHovered
      },
      on: { pointerenter: tableEl.helpers.handleSegmentBlockPointerEnter }
    }, /* @__PURE__ */ jsx("div", {
      sel: `.segment-cell-block-inner`
    }, /* @__PURE__ */ jsx(BodyCell, {
      tableEl,
      row,
      column,
      atSelectionLimit,
      segmentCellBlock: cellBlock
    })));
  });
}
function renderPlaceholderGridAreas({ gridAreas }) {
  return gridAreas.map((gridArea) => {
    const gridRowStart = gridArea.gridRowStartIdx + 1;
    const gridRowEnd = gridRowStart + gridArea.numRows;
    const gridColStart = gridArea.gridColumnStartIdx + 1;
    const gridColEnd = gridColStart + gridArea.numColumns;
    return /* @__PURE__ */ jsx("div", {
      sel: ".placeholder-grid-area",
      style: {
        "grid-area": `${gridRowStart} / ${gridColStart} / ${gridRowEnd} / ${gridColEnd}`
      }
    });
  });
}
function renderFixedMultimetricFooter({ tableEl }) {
  const gridRowEnd = tableEl.getTableEndRowIdx() + 1;
  const gridRowStart = gridRowEnd - 1;
  const gridColStart = 1;
  const gridColEnd = gridColStart + tableEl.state.stickyColumnsCount;
  return /* @__PURE__ */ jsx("div", {
    sel: ".multimetric-footer-cell.overall-cell",
    style: {
      "grid-area": `${gridRowStart} / ${gridColStart} / ${gridRowEnd} / ${gridColEnd}`
    }
  }, "Overall");
}
function renderDataMultimetricFooter({ tableEl, columns }) {
  const gridRowEnd = tableEl.getTableEndRowIdx() + 1;
  const gridRowStart = gridRowEnd - 1;
  return columns.map((column) => {
    const gridColStart = column.subsetColumnIdx + 1;
    const gridColEnd = gridColStart + 1;
    return /* @__PURE__ */ jsx("div", {
      sel: ".multimetric-footer-cell.metric-total-cell",
      style: {
        "grid-area": `${gridRowStart} / ${gridColStart} / ${gridRowEnd} / ${gridColEnd}`
      }
    }, renderDataCellContent({
      comparisonValue: column.comparisonOverall,
      formatAsDecimal: column.hasDecimal,
      liftType: tableEl.attr(`lift-comparison-type`) === LiftType.Time ? LiftType.Time : null,
      liftValue: column.liftOverall,
      value: column.metricOverall
    }));
  });
}
function renderSearchInput({ tableEl }) {
  return /* @__PURE__ */ jsx(InputEl, {
    sel: `.${CssClass.SearchInput}`,
    attrs: {
      icon: `search`,
      placeholder: `Search`,
      size: InputSize.Medium,
      "show-clear": true
    },
    props: {
      value: tableEl.state.searchFilter
    },
    on: {
      clear: () => tableEl.helpers.handleSearchTextUpdate(``),
      input: (ev) => tableEl.helpers.handleSearchTextUpdate(ev.target.value)
    }
  });
}
function getTemplateWidths(cols) {
  return cols.map((c) => `${c.computedWidth}px`).join(` `);
}
export function template() {
  var _a;
  const tableTheme = this.getTheme();
  const gap = this.helpers.getGapSize();
  const groupHeaders = this.getGroupHeadersToRender();
  const fixedColumns = this.getFixedColumns();
  const dataColumns = this.getDataColumns();
  const dataColumnsToRender = this.getDataColumnsToRender();
  const fixedColumnsWidth = getSummedColumnWidth(fixedColumns);
  const internalTableWidth = this.helpers.getInternalTableWidth();
  const hasSegmentCellBlocks = this.showSegmentCellBlocks();
  const canUseResizingLayoutVal = !this.isBoardMode();
  const preTitleRowHeight = this.getPreTitleRowHeight();
  const titleRowHeight = this.getTitleRowHeight();
  const headerRowHeights = this.getHeaderRowHeights();
  const headerHeight = sum(headerRowHeights) + gap;
  const { flatFilteredRows, numRowsToRender, renderRowStartIdx } = this.state;
  const headerDataColumnsToRender = dataColumns;
  const dataColumnWidthsCssVar = getTemplateWidths(headerDataColumnsToRender);
  const dataColumnsWidth = getSummedColumnWidth(headerDataColumnsToRender);
  const flatRowsToRender = this.getFlatRowsToRender();
  const renderRowEndIdx = renderRowStartIdx + numRowsToRender;
  const rowHeightsArray = [
    ...headerRowHeights.map((x) => `${x}px`),
    `${getSummedRowHeight(flatFilteredRows.slice(0, renderRowStartIdx))}px`,
    ...flatRowsToRender.map((row) => `${row.rowHeight}px`),
    `${getSummedRowHeight(flatFilteredRows.slice(renderRowEndIdx))}px`,
    ...this.showMultimetricFooter() ? [`${FOOTER_ROW_HEIGHT}px`] : []
  ];
  const rowHeights = rowHeightsArray.join(` `);
  const useBorders = tableTheme.cellBorders === `line`;
  const {
    numSelected,
    numSelectable,
    atSelectionLimit,
    hasReachedSegmentationLimit
  } = this.helpers.getGlobalSelectionStats();
  const lastFlatRow = last(flatRowsToRender);
  const showTruncatedSegmentsInfo = hasReachedSegmentationLimit && (!lastFlatRow || lastFlatRow.filteredRowIdx === this.state.filteredRows.length - 1);
  const emptySearchResults = this.state.searchFilter && !flatRowsToRender.length;
  const titleRowTopOffset = (preTitleRowHeight || 0) + gap;
  return /* @__PURE__ */ jsx("div", {
    sel: ".wrapper",
    style: {
      "--checkbox-cell-padding-left": `${CHECKBOX_CELL_PADDING_LEFT}px`,
      "--table-horizontal-padding": canUseResizingLayoutVal ? HORIZONTAL_CONTENT_PADDING_STYLE : `0`
    }
  }, this.attr(`multi-select`) && !canUseResizingLayoutVal ? (() => {
    return /* @__PURE__ */ jsx("div", {
      sel: ".segments-info-section"
    }, /* @__PURE__ */ jsx("div", {
      sel: ".truncated-segments-info"
    }, /* @__PURE__ */ jsx("div", {
      sel: ".checkbox-wrapper"
    }, /* @__PURE__ */ jsx(CheckboxEl, {
      sel: ".select-segment-checkbox",
      attrs: {
        checked: atSelectionLimit,
        "unchecked-icon": numSelected > 0 ? `dash` : ``
      },
      style: { width: `auto` },
      on: { click: this.helpers.toggleAllSelected }
    }), /* @__PURE__ */ jsx(TooltipEl, {
      attrs: { placement: Placement.RightCenter }
    }, atSelectionLimit ? `Deselect All` : `Select Top Segments`)), `Selected ${numSelected} of ${hasReachedSegmentationLimit ? `top ` : ``}${numSelectable} segments`, hasReachedSegmentationLimit ? /* @__PURE__ */ jsx(SegmentationLimitInfoEl, {
      attrs: {
        "segmentation-limit": this.attr(`segmentation-limit`),
        "export-segmentation-limit": this.attr(`export-segmentation-limit`)
      }
    }) : null), /* @__PURE__ */ jsx(FilterBarEl, {
      sel: ".filter-bar",
      on: {
        updateInput: (ev) => this.helpers.handleSearchTextUpdate(ev.detail.value)
      }
    }));
  })() : null, this.isSearchEnabled() && !groupHeaders && renderSearchInput({ tableEl: this }), /* @__PURE__ */ jsx("div", {
    sel: ".mp-data-table-container",
    hook: {
      insert: (vnode) => this.scrollParentElInserted(vnode.elm)
    }
  }, /* @__PURE__ */ jsx("div", {
    sel: ".mp-data-table",
    style: {
      "--data-table-columns": `${fixedColumnsWidth}px ${dataColumnsWidth + (useBorders ? 0 : 2 * gap)}px`,
      "--fixed-columns-width": `${fixedColumnsWidth}px`,
      "--gap": `${gap}px`,
      "--body-row-height": `${tableTheme.rowHeight}px`,
      "--sticky-columns-border-width": `${dataColumns.length ? STICKY_COLUMNS_BORDER_WIDTH : 0}px`,
      "--header-height": `${headerHeight}px`,
      "--title-row-top-offset": `${titleRowTopOffset}px`,
      "--time-comparison-header-row-top-offset": `${titleRowTopOffset + titleRowHeight}px`,
      "--table-width": `${internalTableWidth}px`,
      "--row-heights": rowHeights
    },
    class: {
      "use-borders": useBorders,
      "has-group-headers": Boolean(groupHeaders),
      heatmap: tableTheme.cellBackground === `heatmap`,
      "cell-outline-on-hover": tableTheme.cellBorderHover === `outline`,
      "multimetric-styles": tableTheme.multimetricStyles,
      "has-segment-cell-blocks": hasSegmentCellBlocks,
      "resizing-layout": canUseResizingLayoutVal,
      "show-info-footer-container": showTruncatedSegmentsInfo || emptySearchResults,
      "grouped-sort": ((_a = this.state.sorting) == null ? void 0 : _a.sortBy) === SortBy.Column,
      "can-use-view-n": this.canUseViewN(),
      "has-time-comparison-headers": this.hasTimeComparisonHeaders()
    },
    on: {
      scroll: this.helpers.handleScroll
    },
    hook: {
      insert: (vnode) => this.scrollElInserted(vnode.elm)
    }
  }, /* @__PURE__ */ jsx("div", {
    sel: `.${CssClass.FixedColumns}`,
    class: {
      "sticky-columns": internalTableWidth - fixedColumnsWidth >= Math.min(MIN_DATA_COLUMNS_VIEWPORT_WIDTH, dataColumnsWidth)
    },
    style: { "--fixed-column-widths": getTemplateWidths(fixedColumns) }
  }, renderPlaceholderGridAreas({ gridAreas: this.getFixedPlaceholderGridAreas() }), groupHeaders ? /* @__PURE__ */ jsx("div", {
    sel: ".group-header-row-cell",
    style: {
      "grid-row": `1 / 2`,
      "grid-column": `1 / ${fixedColumns.length + 1}`
    }
  }, this.isSearchEnabled() && renderSearchInput({ tableEl: this })) : null, fixedColumns.map((column) => /* @__PURE__ */ jsx(TitleCell, {
    tableEl: this,
    column
  })), this.hasTimeComparisonHeaders() && fixedColumns.map((column) => /* @__PURE__ */ jsx(TimeComparisonHeaderCell, {
    column,
    tableEl: this
  })), renderSegmentCellBlocks({ tableEl: this }), renderBodyRows({
    tableEl: this,
    rows: flatRowsToRender,
    columns: fixedColumns,
    atSelectionLimit
  }), this.showMultimetricFooter() ? renderFixedMultimetricFooter({ tableEl: this }) : null, renderResizeHandles({
    tableEl: this,
    columns: fixedColumns,
    gap,
    idxOffset: 0,
    resizingColumnIdx: this.state.resizeColumnIndex,
    resizeHandleTopOffset: preTitleRowHeight || 0
  })), /* @__PURE__ */ jsx("div", {
    sel: `.${CssClass.ScrollableColumns}`,
    style: { "--data-column-widths": dataColumnWidthsCssVar },
    class: { "is-resizing": Boolean(this.state.resizeColumnIndex) }
  }, renderPlaceholderGridAreas({ gridAreas: this.getDataPlaceholderGridAreas() }), groupHeaders ? groupHeaders.reduce(
    (acc, { title, span, funnelStepNumber }, idx) => {
      const context = {
        rowType: RowType.GroupHeader,
        columnIdx: acc.colStart - 1
      };
      const shouldShowTooltip = isSameCellByContext(context, this.state.hoveredCellContext) && Boolean(this.state.hoveredTruncatedTextType);
      const tooltipEl = shouldShowTooltip ? /* @__PURE__ */ jsx(TooltipEl, {
        attrs: {
          "max-width": `${GROUP_HEADER_MIN_WIDTH}px`,
          placement: Placement.Bottom,
          offset: 0,
          "always-show": true
        }
      }, title) : null;
      return {
        nodes: [
          ...acc.nodes,
          /* @__PURE__ */ jsx("div", {
            sel: ".group-header-row-cell.group-header",
            class: { "last-group-header": idx === groupHeaders.length - 1 },
            style: {
              "grid-row": `1 / 2`,
              "grid-column": `${acc.colStart} / ${acc.colStart + span}`
            }
          }, /* @__PURE__ */ jsx("span", {
            sel: ".group-header-label",
            on: {
              pointerenter: (ev) => {
                this.helpers.handleCellPointerEnter(context);
                this.helpers.handleTruncatableTextPointerEnter(ev);
              },
              pointerleave: this.helpers.handleCellPointerLeave
            }
          }, funnelStepNumber ? /* @__PURE__ */ jsx("span", {
            sel: ".step-number"
          }, funnelStepNumber) : null, title), tooltipEl)
        ],
        colStart: acc.colStart + span
      };
    },
    { nodes: [], colStart: 1 }
  ).nodes : null, headerDataColumnsToRender.map((col) => /* @__PURE__ */ jsx(TitleCell, {
    tableEl: this,
    column: col
  })), this.hasTimeComparisonHeaders() && dataColumns.map((column) => /* @__PURE__ */ jsx(TimeComparisonHeaderCell, {
    column,
    tableEl: this
  })), renderBodyRows({
    tableEl: this,
    rows: flatRowsToRender,
    columns: dataColumnsToRender,
    atSelectionLimit
  }), this.showMultimetricFooter() ? renderDataMultimetricFooter({ tableEl: this, columns: dataColumnsToRender }) : null), canUseResizingLayoutVal && (showTruncatedSegmentsInfo || emptySearchResults) ? /* @__PURE__ */ jsx("div", {
    sel: ".info-footer-container"
  }, emptySearchResults ? /* @__PURE__ */ jsx("div", {
    sel: ".empty-search-results"
  }, "Your search returned no results") : null, showTruncatedSegmentsInfo ? /* @__PURE__ */ jsx(BannerEl, {
    sel: ".truncated-segments-banner",
    hook: {
      insert: (vnode) => this.helpers.onInsertTruncatedSegmentsInfo(vnode.elm),
      destroy: () => this.helpers.onDestroyTruncatedSegmentsInfo()
    },
    attrs: {
      size: BannerSize.Small,
      type: BannerType.Embedded,
      theme: BannerTheme.Base,
      "banner-tracking-type": `[Data Table] Truncated Segments`
    }
  }, /* @__PURE__ */ jsx("div", {
    sel: ".truncated-segments-text",
    attrs: { slot: `body-text` }
  }, `We limit the number of segments to ${this.attr(`segmentation-limit`).toLocaleString()}. `, /* @__PURE__ */ jsx("a", {
    attrs: { href: SegmentationLimitLearnMoreUrl, target: `_blank` }
  }, "Learn more"), ` or `, /* @__PURE__ */ jsx("a", {
    attrs: { href: `javascript:void(0);` },
    on: {
      click: () => this.dispatchEvent(new CustomEvent(`export-csv`, { bubbles: true, composed: true }))
    }
  }, "export up to ", this.attr(`export-segmentation-limit`).toLocaleString(), " segments via CSV"))) : null) : null)));
}
